/**
 * Microsoft 365 カレンダー API サービス
 *
 * バックエンドのM365エンドポイントを呼び出してカレンダーデータを取得。
 */

import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';

// M365専用のAxiosインスタンス
const m365Api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// トークンプロバイダー関数（MSAL acquireTokenSilent をラップ）
let _tokenProvider = null;

/**
 * トークンプロバイダーを設定
 * 毎リクエスト前に呼び出され、最新のトークンを返す関数を登録する。
 * MSALの acquireTokenSilent は内部キャッシュを使用するため、
 * 有効なトークンがある場合はネットワーク呼び出しは発生しない。
 * @param {(() => Promise<string|null>)|null} provider - トークン取得関数
 */
export const setM365TokenProvider = (provider) => {
  _tokenProvider = provider;
};

// リクエストインターセプター: 毎回最新のトークンを取得して付与
m365Api.interceptors.request.use(async (config) => {
  if (_tokenProvider) {
    const token = await _tokenProvider();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // トークン取得に失敗した場合、リクエストを中断
      const error = new Error('セッションの有効期限が切れました。再ログインしてください。');
      error.isSessionExpired = true;
      throw error;
    }
  }
  return config;
});

// レスポンスインターセプター（エラーハンドリング）
m365Api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      // 許可されていないテナント
      const customError = new Error('このアプリケーションへのアクセスが許可されていません。\n許可された組織のアカウントでログインしてください。');
      customError.isForbidden = true;
      throw customError;
    }
    if (error.response?.status === 401) {
      // 認証エラー
      const customError = new Error('認証が必要です。ログインしてください。');
      customError.isUnauthorized = true;
      throw customError;
    }
    throw error;
  }
);

/**
 * M365 カレンダー API
 */
export const m365CalendarAPI = {
  /**
   * ヘルスチェック
   */
  healthCheck: async () => {
    const response = await m365Api.get('/api/m365/health');
    return response.data;
  },

  /**
   * カレンダーイベント取得（日付範囲指定）
   * @param {string} startDate - 開始日 (YYYY-MM-DD)
   * @param {string} endDate - 終了日 (YYYY-MM-DD)
   * @param {string} room - 会議室タイプ (A/B/null)
   */
  getCalendar: async (startDate, endDate, room = null) => {
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    if (room) {
      params.room = room;
    }
    const response = await m365Api.get('/api/m365/calendar', { params });
    return response.data;
  },

  /**
   * カレンダーイベント取得（月指定）
   * 既存のフロントエンド互換
   * @param {number} year - 年
   * @param {number} month - 月
   * @param {string} room - 会議室タイプ (A/B/null)
   */
  getCalendarByMonth: async (year, month, room = null) => {
    const params = room ? { room } : {};
    const response = await m365Api.get(`/api/m365/calendar/${year}/${month}`, { params });
    return response.data;
  },
};

/**
 * お知らせ API
 */
export const announcementAPI = {
  getAnnouncements: async () => {
    const response = await m365Api.get('/api/announcements');
    return response.data;
  },

  createAnnouncement: async (title, content) => {
    const response = await m365Api.post('/api/announcements', { title, content });
    return response.data;
  },

  deleteAnnouncement: async (id) => {
    await m365Api.delete(`/api/announcements/${id}`);
  },
};

export default m365Api;

